/** Die Kacheln des Überblicks von "Schule" (BE-04). Daten kommen aus `useSchuleDaten`. */
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';

import { db } from '@/db/schema';
import { StatKarte, Kennzahl, KennzahlZeile } from '@/ui/composites/Statistik';
import { countdown, klausurDatum } from '@/domain/school/exams';
import { formatNote } from '@/domain/school/grades';
import { dauerText, lernzeitProFach } from '@/domain/school/studyTime';
import { schuljahrBeginn } from '@/domain/stats/schule';
import type { StudySession } from '@/domain/types';
import { Stundenplan } from '@/modules/school/components/Stundenplan';
import { Noten } from '@/modules/school/components/Noten';
import { NotenVerlaufDiagramm } from '@/modules/school/components/SchulStatistik';
import { Hausaufgaben } from '@/modules/school/components/Hausaufgaben';
import { Klausuren } from '@/modules/school/components/Klausuren';
import { Lernzeit } from '@/modules/school/components/Lernzeit';
import { FaecherKarten, HeuteSchule } from '@/modules/school/components/SchuleBausteine';
import { useSchuleDaten } from '@/modules/school/useSchuleDaten';
import { startOfWeek } from '@/lib/datetime';

/** Die vier Kennzahlen als Zeile - oben auf der Seite des Bereichs (D-00c). */
export function SchuleKennzahlenZeile({ spalten = 4 }: { spalten?: 2 | 3 | 4 }) {
  const d = useSchuleDaten();
  const sitzungen = useLiveQuery(() => db.studySessions.toArray(), [], [] as StudySession[]);
  const minuten = [...lernzeitProFach(sitzungen, new Date(), startOfWeek(d.heute)).values()].reduce((a, b) => a + b, 0);
  const k = d.naechsteKlausur;
  return (
    <KennzahlZeile spalten={spalten}>
        <Kennzahl label="Schnitt" wert={d.gesamt === null ? '–' : formatNote(d.gesamt, d.system)} hinweis={d.gesamt === null ? 'noch keine Noten' : d.system === 'punkte' ? 'Punkte 0–15' : 'Noten 1–6'} href="/schule/noten" />
        <Kennzahl label="Nächste Klausur" wert={k ? countdown(d.heute, klausurDatum(k)) : '–'} hinweis={k ? k.title : 'keine angesetzt'} href="/schule/klausuren" />
        <Kennzahl label="Offene Hausaufgaben" wert={String(d.offen.length)} hinweis={d.offen.length ? 'noch zu erledigen' : 'alles erledigt'} href="/schule/faecher" />
        <Kennzahl label="Lernzeit" wert={dauerText(minuten)} hinweis="diese Woche" href="/schule/faecher" />
      </KennzahlZeile>
  );
}

export function SchuleKennzahlenKachel() {
  return (
    <StatKarte titel="Kennzahlen" frage="Wie steht es um Noten, Klausuren und Lernen?">
      <SchuleKennzahlenZeile spalten={2} />
    </StatKarte>
  );
}

export function SchuleHeuteKachel() { return <HeuteSchule d={useSchuleDaten()} />; }
export function SchuleFaecherKachel() { return <FaecherKarten d={useSchuleDaten()} />; }

export function SchuleStundenplanKachel() {
  const d = useSchuleDaten();
  return d.settings ? <Stundenplan settings={d.settings} faecher={d.faecher} stunden={d.stunden} /> : null;
}

export function SchuleNotenKachel() {
  const d = useSchuleDaten();
  return d.settings ? <Noten faecher={d.faecher} noten={d.noten} system={d.system} /> : null;
}

export function SchuleVerlaufKachel() {
  const d = useSchuleDaten();
  return (
    <StatKarte titel="Notenverlauf" frage="Wie entwickeln sich meine Noten seit Beginn des Schuljahres?"
      aktion={<Link to="/schule/noten" className="text-[12px] text-accent-text hover:underline">Mehr</Link>}>
      <NotenVerlaufDiagramm noten={d.noten} faecher={d.faecher} system={d.system} von={schuljahrBeginn(d.heute)} bis={d.heute} />
    </StatKarte>
  );
}

export function SchuleHausaufgabenKachel() { const d = useSchuleDaten(); return <Hausaufgaben faecher={d.faecher} areaId={d.settings?.areaId ?? null} />; }
export function SchuleKlausurenKachel() { return <Klausuren faecher={useSchuleDaten().faecher} />; }
export function SchuleLernzeitKachel() { return <Lernzeit faecher={useSchuleDaten().faecher} />; }
